import { ref } from 'vue';
import { useWalletStore } from '@/store/wallet';

export function useCardFormValidation() {
    const errors = ref({});
    const errorsList = ref([]);

    const walletStore = useWalletStore();
    const { showNotification } = walletStore;

    const validateUpdateCardForm = (formData) => {
        errors.value = {};
        errorsList.value = [];

        const cardData = formData.card;
        const welcomeBonusData = formData.welcome_bonus;
        const tieredBonusesData = formData.tiered_bonuses;
        const bonusOffersData = formData.bonus_offers;

        if (cardData.card_not_found && !cardData.card) {
            if (!cardData.card_not_found_name) {
                errors.value.card_not_found_name =
                    'Please enter the name of your card.';
                errorsList.value.push({
                    message: 'Please enter the name of your card.',
                });
            }

            if (!cardData.card_not_found_bank) {
                errors.value.card_not_found_bank =
                    'Please enter the card issuing bank.';
                errorsList.value.push({
                    message: 'Please enter the card issuing bank.',
                });
            }
        } else {
            if (!cardData.ignore && (!cardData.card || !cardData.card.id)) {
                errors.value.card_id = 'Please choose a card.';
                errorsList.value.push({ message: 'Please choose a card.' });
            }

            if (!cardData.account_type || cardData.account_type === '') {
                errors.value.account_type = 'Please choose an account type.';
                errorsList.value.push({
                    message: 'Please choose an account type.',
                });
            }

            if (
                !cardData.ignore &&
                !cardData.opened_long_ago &&
                (!cardData.date_opened || cardData.date_opened === '')
            ) {
                errors.value.date_opened = 'Please enter the date opened.';
                errorsList.value.push({
                    message: 'Please enter the date opened.',
                });
            }

            if (
                !cardData.ignore &&
                (!cardData.card_status || cardData.card_status === '')
            ) {
                errors.value.card_status = 'Please choose the card status.';
                errorsList.value.push({
                    message: 'Please choose the card status.',
                });
            }

            // Welcome Bonus validation
            if (welcomeBonusData && welcomeBonusData.type) {
                if (welcomeBonusData.type !== 'tiered') {
                    if (!welcomeBonusData.bonus_amount) {
                        errors.value.bonus_amount =
                            'Please enter the bonus amount.';
                        errorsList.value.push({
                            message: 'Please enter the welcome bonus amount.',
                        });
                    }

                    if (!welcomeBonusData.minimum_spend_amount) {
                        errors.value.minimum_spend_amount =
                            'Please enter the minimum spend amount.';
                        errorsList.value.push({
                            message: 'Please enter the minimum spend amount.',
                        });
                    }

                    if (
                        welcomeBonusData.received &&
                        !welcomeBonusData.date_received
                    ) {
                        errors.value.date_received =
                            'Please enter the date the welcome bonus was received.';
                        errorsList.value.push({
                            message:
                                'Please enter the date the welcome bonus was received.',
                        });
                    }

                    if (
                        !welcomeBonusData.received &&
                        !welcomeBonusData.minimum_spend_deadline
                    ) {
                        errors.value.minimum_spend_deadline =
                            'Please enter the spend deadline for welcome bonus.';
                        errorsList.value.push({
                            message:
                                'Please enter the spend deadline for welcome bonus.',
                        });
                    }
                } else if (welcomeBonusData.type === 'tiered') {
                    if (!tieredBonusesData || tieredBonusesData.length === 0) {
                        errors.value.tiered_bonuses =
                            'Please add at least one tier for the tiered bonus.';
                        errorsList.value.push({
                            message:
                                'Please add at least one tier for the tiered bonus.',
                        });
                    } else {
                        tieredBonusesData.forEach((tier, index) => {
                            if (!tier.bonus_amount) {
                                errors.value[`tier_${index}_bonus_amount`] =
                                    `Please enter the bonus amount for tier ${index + 1}.`;
                                errorsList.value.push({
                                    message: `Please enter the bonus amount for tier ${index + 1}.`,
                                });
                            }

                            if (!tier.minimum_spend_amount) {
                                errors.value[
                                    `tier_${index}_minimum_spend_amount`
                                ] =
                                    `Please enter the minimum spend amount for tier ${index + 1}.`;
                                errorsList.value.push({
                                    message: `Please enter the minimum spend amount for tier ${index + 1}.`,
                                });
                            }

                            if (tier.received && !tier.date_received) {
                                errors.value[`tier_${index}_date_received`] =
                                    `Please enter the date received for tier ${index + 1}.`;
                                errorsList.value.push({
                                    message: `Please enter the date received for tier ${index + 1}.`,
                                });
                            }

                            if (
                                !tier.received &&
                                !tier.minimum_spend_deadline
                            ) {
                                errors.value[
                                    `tier_${index}_minimum_spend_deadline`
                                ] =
                                    `Please enter the spend deadline for tier ${index + 1}.`;
                                errorsList.value.push({
                                    message: `Please enter the spend deadline for tier ${index + 1}.`,
                                });
                            }
                        });
                    }
                }
            }

            // Bonus Offers validation
            if (bonusOffersData && bonusOffersData.length > 0) {
                bonusOffersData.forEach((offer, index) => {
                    if (!offer.type) {
                        errors.value[`offer_${index}_type`] =
                            `Please select a type for offer ${index + 1}.`;
                        errorsList.value.push({
                            message: `Please select a type for offer ${index + 1}.`,
                        });
                    } else if (offer.type !== 'other') {
                        if (!offer.bonus_amount) {
                            errors.value[`offer_${index}_bonus_amount`] =
                                `Please enter the bonus amount for offer ${index + 1}.`;
                            errorsList.value.push({
                                message: `Please enter the bonus amount for offer ${index + 1}.`,
                            });
                        }

                        if (!offer.bonus_units) {
                            errors.value[`offer_${index}_bonus_units`] =
                                `Please select bonus units for offer ${index + 1}.`;
                            errorsList.value.push({
                                message: `Please select bonus units for offer ${index + 1}.`,
                            });
                        }

                        if (offer.received && !offer.date_received) {
                            errors.value[`offer_${index}_date_received`] =
                                `Please enter the date received for offer ${index + 1}.`;
                            errorsList.value.push({
                                message: `Please enter the date received for offer ${index + 1}.`,
                            });
                        }

                        if (!offer.received && !offer.minimum_spend_deadline) {
                            errors.value[
                                `offer_${index}_minimum_spend_deadline`
                            ] =
                                `Please enter the spend deadline for offer ${index + 1}.`;
                            errorsList.value.push({
                                message: `Please enter the spend deadline for offer ${index + 1}.`,
                            });
                        }
                    }
                });
            }
        }
    };

    const validateOriginalBonusOffers = (
        isProductChanged,
        originalBonusOffers
    ) => {
        if (
            isProductChanged &&
            originalBonusOffers &&
            originalBonusOffers.length > 0
        ) {
            originalBonusOffers.forEach((offer, index) => {
                const isOfferStarted =
                    offer.type ||
                    offer.bonus_amount ||
                    offer.bonus_units ||
                    offer.minimum_spend_amount ||
                    offer.minimum_spend_deadline ||
                    offer.date_received ||
                    offer.received;

                if (!isOfferStarted) {
                    return;
                }
                if (!offer.type) {
                    errors.value[`original_offer_${index}_type`] =
                        `Please select a type for original offer ${index + 1}.`;
                    errorsList.value.push({
                        message: `Please select a type for original offer ${index + 1}.`,
                    });
                } else if (offer.type !== 'other') {
                    if (!offer.bonus_amount) {
                        errors.value[`original_offer_${index}_bonus_amount`] =
                            `Please enter the bonus amount for original offer ${index + 1}.`;
                        errorsList.value.push({
                            message: `Please enter the bonus amount for original offer ${index + 1}.`,
                        });
                    }

                    if (!offer.bonus_units) {
                        errors.value[`original_offer_${index}_bonus_units`] =
                            `Please select bonus units for original offer ${index + 1}.`;
                        errorsList.value.push({
                            message: `Please select bonus units for original offer ${index + 1}.`,
                        });
                    }

                    if (offer.received && !offer.date_received) {
                        errors.value[`original_offer_${index}_date_received`] =
                            `Please enter the date received for original offer ${index + 1}.`;
                        errorsList.value.push({
                            message: `Please enter the date received for original offer ${index + 1}.`,
                        });
                    }

                    if (!offer.received && !offer.minimum_spend_deadline) {
                        errors.value[
                            `original_offer_${index}_minimum_spend_deadline`
                        ] =
                            `Please enter the spend deadline for original offer ${index + 1}.`;
                        errorsList.value.push({
                            message: `Please enter the spend deadline for original offer ${index + 1}.`,
                        });
                    }
                }
            });
        }
    };

    const handleSaveError = (
        error,
        defaultMessage = 'Your card details could not be saved.'
    ) => {
        let errorMessage = defaultMessage;
        let errorTitle = 'Save Failed';

        if (error.response) {
            if (error.response.status === 422) {
                errorTitle = 'Validation Error';
                errorMessage =
                    error.response.data?.message ||
                    'Please check your input and try again.';
            } else if (error.response.status === 500) {
                errorTitle = 'Server Error';
                errorMessage =
                    'A server error occurred. Please try again later.';
            } else if (error.response.status === 403) {
                errorTitle = 'Access Denied';
                errorMessage =
                    'You do not have permission to perform this action.';
            } else {
                errorMessage =
                    error.response.data?.message ||
                    error.message ||
                    errorMessage;
            }
        } else if (error.request) {
            errorTitle = 'Network Error';
            errorMessage =
                'Unable to connect to the server. Please check your internet connection.';
        } else {
            errorMessage = error.message || errorMessage;
        }

        showNotification(errorTitle, errorMessage, 10000, 'error');
    };

    const clearError = (key) => {
        if (key in errors.value) {
            delete errors.value[key];
        }
    };

    return {
        errors,
        errorsList,
        validateUpdateCardForm,
        validateOriginalBonusOffers,
        handleSaveError,
        clearError,
    };
}
